// frontend/src/components/modals/OsNsaFormModal.js
import React, { useState, useEffect, useRef } from 'react';
import { 
    X, Plus, Trash2, Save, Loader2, Search, User, MapPin, Mail, 
    Calendar, Package, FileText, ClipboardList, Lock, 
    Unlock, Hash, ExternalLink, DollarSign, CheckCircle, 
    Info, Folder, Edit, Printer, FileCheck, Zap, Shield, Image as ImageIcon,
    Globe, Camera, ChevronRight, Check, ChevronDown
} from 'lucide-react';
import useApi from '../../hooks/useApi';
import { useReactToPrint } from 'react-to-print';

// Importa os componentes visuais dos Laudos
import LaudoTensaoAplicada from '../technical_reports/LaudoTensaoAplicada';
import LaudoTensaoAplicadaCatB from '../technical_reports/LaudoTensaoAplicadaCatB'; 
import LaudoEpis from '../technical_reports/LaudoEpis';

// --- Hook useDebounce ---
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

// --- Helpers de Data ---
const formatDateToBR = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-'); 
    if (parts.length !== 3) return dateString;
    return `${parts[2]}/${parts[1]}/${parts[0]}`; 
};

const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const safeDateString = dateString.replace(' ', 'T');
    const date = new Date(safeDateString);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
};

const formatDateForSubmission = (dateInput) => {
    if (!dateInput || dateInput === 'À COMBINAR') return dateInput;
    if (typeof dateInput === 'string' && dateInput.includes('T')) {
        return dateInput.split('T')[0]; 
    }
    return dateInput;
};

const getTodayLocalISO = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// --- Helper Imagem Direta ---
const getDirectImageUrl = (url) => {
    if (!url) return '';
    if (url.match(/\.(jpeg|jpg|gif|png)$/) != null) return url; 
    const idMatch = url.match(/\/d\/(.+?)\//) || url.match(/id=(.+?)(&|$)/);
    if (idMatch && idMatch[1]) {
        return `https://lh3.googleusercontent.com/d/$${idMatch[1]}=s3000`;
    }
    return url;
};

const formatAddressString = (addr) => {
    if (!addr) return '';
    const parts = [];
    if (addr.address_street) {
        let streetStr = addr.address_street;
        if (addr.address_number) streetStr += `, ${addr.address_number}`;
        parts.push(streetStr);
    }
    if (addr.address_city) {
        let cityStr = addr.address_city;
        if (addr.address_state) cityStr += ` - ${addr.address_state}`;
        parts.push(cityStr);
    }
    return parts.join(' | ');
};

// --- Componente: Busca de Serviços ---
const ServiceSearchInput = ({ value, onChange, onSelect, services, clientPrices, disabled, hideSensitiveData }) => {
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef(null);

    const safeServices = Array.isArray(services) ? services : [];
    const filteredServices = safeServices.filter(s => 
        s.description.toLowerCase().includes((value || '').toLowerCase())
    );

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <input
                type="text"
                className={`w-full p-1 border-b border-transparent focus:border-blue-500 focus:outline-none ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
                placeholder="Busque um serviço..."
                value={value}
                onChange={(e) => {
                    onChange(e.target.value);
                    setShowSuggestions(true);
                }}
                onFocus={() => !disabled && setShowSuggestions(true)}
                required
                disabled={disabled}
            />
            {showSuggestions && filteredServices.length > 0 && !disabled && (
                <ul className="absolute z-50 w-full min-w-[200px] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1 left-0">
                    {filteredServices.map((service) => {
                        const serviceIdStr = String(service.id);
                        const price = clientPrices && clientPrices[serviceIdStr] !== undefined 
                            ? parseFloat(clientPrices[serviceIdStr]) 
                            : 0;
                        const hasPrice = clientPrices && clientPrices[serviceIdStr] !== undefined;

                        return (
                            <li
                                key={service.id}
                                onClick={() => {
                                    onSelect(service, price);
                                    setShowSuggestions(false);
                                }}
                                className="px-4 py-2 cursor-pointer hover:bg-gray-100 flex justify-between items-center"
                            >
                                <span className="text-sm text-gray-800">{service.description}</span>
                                {hasPrice && (
                                    <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                        {hideSensitiveData 
                                            ? '******' 
                                            : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)
                                        }
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

// --- Componente Principal: Modal ---
const OsNsaFormModal = ({ osToEdit, onClose, onSave, onFaturar, currentUser, formatCurrency, hideSensitiveData, initialStatus }) => {
    const { apiFetch, permissions: apiPermissions, userProfile: apiUserProfile } = useApi(); 
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    
    const activeUser = currentUser || apiUserProfile;
    const activePermissions = activeUser?.permissions || apiPermissions || {};
    const isSuper = activeUser?.is_super_user;
    
    const isClient = activeUser?.is_client;
    
    const [activeTab, setActiveTab] = useState('geral');
    
    // --- ESTADOS DE LAUDOS ---
    const [technicalReports, setTechnicalReports] = useState([]);
    const [showReportEditor, setShowReportEditor] = useState(false);
    const [currentReport, setCurrentReport] = useState(null);
    const [reportType, setReportType] = useState('TENSAO_APLICADA');
    
    // --- ESTADOS DA ABA DE FOTOS ---
    const [selectedReportIdForPhotos, setSelectedReportIdForPhotos] = useState(''); 
    const [editingPhotosForReport, setEditingPhotosForReport] = useState(null); 
    
    const [showReportTypeSelection, setShowReportTypeSelection] = useState(false);
    const [laudoStep, setLaudoStep] = useState('type'); 
    const [tempSelectedType, setTempSelectedType] = useState('');

    const [reportToPrint, setReportToPrint] = useState(null);
    const reportPrintRef = useRef();
    
    // Estado para controlar a abertura/fechamento dos grupos de laudos
    const [expandedGroups, setExpandedGroups] = useState({});

    // --- FUNÇÃO PARA GERER O NOME DO ARQUIVO PDF ---
    const getReportFileName = () => {
        if (!reportToPrint) return 'Laudo_Tecnico';
        const d = reportToPrint.data || {};

        const clean = (str) => (str || '').toString().replace(/[\\/:*?"<>|]/g, '').trim().replace(/\s+/g, '_');
        
        let dateStr = '00-00-0000';
        const rawDate = (reportToPrint.created_at || '').toString();
        
        if (rawDate.length >= 10 && rawDate.match(/^\d{4}-\d{2}-\d{2}/)) {
            const yyyy = rawDate.substring(0, 4);
            const mm = rawDate.substring(5, 7);
            const dd = rawDate.substring(8, 10);
            dateStr = `${dd}-${mm}-${yyyy}`;
        } else {
            dateStr = formatDateToBR(getTodayLocalISO()).replace(/\//g, '-');
        }

        const client = clean(d.cliente_nome || 'Cliente');
        const os = clean(d.referencia || d.pedido || 'OS');

        if (reportToPrint.report_type === 'TENSAO_APLICADA' || reportToPrint.report_type === 'TENSAO_APLICADA_CAT_B') {
            const placa = clean(d.equipamento_placa || 'S_Placa');
            const chassi = clean(d.equipamento_serie || 'S_Chassi');
            const serie = clean(d.lanca_serie || 'S_Serie');
            return `${client}_Laudo_${os}_${placa}_${chassi}_${serie}_${dateStr}`;
        } else {
            const item = clean(d.item_servico_selecionado || 'Item');
            const lotes = [];
            for (let i = 0; i < 100; i++) { if (d[`item_${i}_lote`]) lotes.push(d[`item_${i}_lote`]); }
            let loteInfo = 'S_Lote';
            if (lotes.length > 0) {
                const first = lotes[0];
                const last = lotes[lotes.length - 1];
                loteInfo = lotes.every(l => l === first) ? first : `${first}-${last}`;
            }
            return `${client}_Laudo_${os}_${item}_${clean(loteInfo)}_${dateStr}`;
        }
    };

    const handlePrintReport = useReactToPrint({
        content: () => reportPrintRef.current,
        documentTitle: getReportFileName(),
        onAfterPrint: () => setReportToPrint(null),
        pageStyle: `
            @page { size: A4 portrait; margin: 5mm; }
            @media print { 
                body { -webkit-print-color-adjust: exact; }
                html, body { width: 100%; height: 100%; margin: 0; padding: 0; }
            }
        `
    });

    useEffect(() => {
        if (reportToPrint) {
            setTimeout(() => {
                handlePrintReport();
            }, 200); 
        }
    }, [reportToPrint, handlePrintReport]);

    const isEditing = !!(osToEdit && osToEdit.id);
    const isRestricted = hideSensitiveData;
    const isFaturada = osToEdit?.faturada === 1;

    // --- PERMISSÕES ---
    const canViewGeral = !isClient && (isSuper || activePermissions?.os_tab_geral_view);
    const hasCreatePermission = isSuper || activePermissions?.comercial_os_create;
    const canEditGeral = !isClient && !isFaturada && (isSuper || activePermissions?.os_tab_geral_edit || (!isEditing && hasCreatePermission));
    const canViewFotos = !isClient && (isSuper || activePermissions?.os_tab_fotos_view);
    const canEditFotos = !isClient && !isFaturada && (isSuper || activePermissions?.os_tab_fotos_edit);
    const canViewLaudos = isClient || isSuper || activePermissions?.os_tab_laudos_tecnicos_view;
    const canEditLaudos = !isClient && !isFaturada && (isSuper || activePermissions?.os_tab_laudos_tecnicos_edit);
    const canShowFaturar = !isClient && (isSuper || activePermissions?.comercial_os_faturar_btn);
    const canShowLiberarFaturamento = !isClient && (isSuper || activePermissions?.comercial_os_liberar_faturamento_btn);
    // --- NOVA TRAVA: Botão de Imprimir Laudo ---
    const canPrintLaudo = isClient || isSuper || activePermissions?.comercial_os_print_laudo;

    useEffect(() => {
        if (activeUser) {
            if (activeTab === 'geral' && !canViewGeral) {
                 if (canViewLaudos) setActiveTab('laudos_tecnicos');
                 else if (canViewFotos) setActiveTab('fotos');
            }
        }
    }, [canViewGeral, canViewFotos, canViewLaudos, activeUser, activeTab]);

    const [logoUrl, setLogoUrl] = useState(null);
    const [clientId, setClientId] = useState(osToEdit?.cliente_id || '');
    const [clientSearch, setClientSearch] = useState(osToEdit?.cliente_nome || '');
    const [clienteEndereco, setClienteEndereco] = useState(osToEdit?.cliente_endereco || '');
    const [clientAddressesOptions, setClientAddressesOptions] = useState([]);
    const [contatoNome, setContatoNome] = useState(osToEdit?.contato_nome || '');
    const [contatoEmail, setContatoEmail] = useState(osToEdit?.contato_email || '');
    
    const [osStatus, setOsStatus] = useState(initialStatus || osToEdit?.os_status || 'Em análise');

    const [dataEmissao, setDataEmissao] = useState(() => {
        if (osToEdit && osToEdit.data_criacao) return osToEdit.data_criacao.split('T')[0];
        return getTodayLocalISO();
    });

    const [dataPrevista, setDataPrevista] = useState(() => {
        if (osToEdit?.data_prevista) {
            if (osToEdit.data_prevista.includes('T')) return osToEdit.data_prevista.split('T')[0];
            return osToEdit.data_prevista;
        }
        return '';
    });

    const [diasConclusao, setDiasConclusao] = useState(osToEdit?.dias_conclusao || '');
    const [deslocamentoKm, setDeslocamentoKm] = useState(osToEdit?.deslocamento_km || '');
    const [totalLaudos, setTotalLaudos] = useState(osToEdit?.total_laudos || '');
    const [faturamentoUnlocked, setFaturamentoUnlocked] = useState(osToEdit?.faturamento_unlocked === 1 || false);

    const [servicos, setServicos] = useState(osToEdit?.servicos && osToEdit.servicos.length > 0 ? osToEdit.servicos.map(s => ({
        ...s, tempId: Math.random() 
    })) : [{ tempId: Math.random(), descricao: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }]);

    const [clientSuggestions, setClientSuggestions] = useState([]);
    const [showClientSuggestions, setShowClientSuggestions] = useState(false);
    const debouncedClientSearch = useDebounce(clientSearch, 300);
    const wrapperRef = useRef(null);
    const [allServices, setAllServices] = useState([]); 
    const [clientPrices, setClientPrices] = useState({}); 

    useEffect(() => {
        apiFetch('/service-table').then(data => setAllServices(data || [])).catch(() => {});

        const init = async () => {
            try {
                if (currentUser?.entityId) {
                    try {
                        const token = localStorage.getItem('token');
                        const logoRes = await fetch(`/api/entities/${currentUser.entityId}/logo`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (logoRes.ok) {
                            const blob = await logoRes.blob();
                            setLogoUrl(URL.createObjectURL(blob));
                        }
                    } catch (e) {}
                }

                if (osToEdit && osToEdit.id) {
                    const fullOs = await apiFetch(`/os-nsa/${osToEdit.id}`);
                    setClientId(fullOs.cliente_id);
                    setClientSearch(fullOs.cliente_nome); 
                    setContatoNome(fullOs.contato_nome || '');
                    setContatoEmail(fullOs.contato_email || '');
                    setClienteEndereco(fullOs.cliente_endereco || '');
                    setOsStatus(fullOs.os_status || 'Em análise');
                    
                    if (fullOs.cliente_id) {
                        try {
                            const clientData = await apiFetch(`/general-clients/${fullOs.cliente_id}`);
                            if (clientData && clientData.addresses && clientData.addresses.length > 0) {
                                setClientAddressesOptions(clientData.addresses);
                            }
                        } catch(e) {}
                    }

                    if (fullOs.data_criacao) {
                        const datePart = fullOs.data_criacao.toString().split('T')[0];
                        setDataEmissao(datePart);
                    }

                    setDataPrevista(fullOs.data_prevista || '');
                    setDiasConclusao(fullOs.dias_conclusao || '');
                    setDeslocamentoKm(fullOs.deslocamento_km || '');
                    setTotalLaudos(fullOs.total_laudos || '');
                    setFaturamentoUnlocked(fullOs.faturamento_unlocked === 1);

                    if (fullOs.servicos && fullOs.servicos.length > 0) {
                        setServicos(fullOs.servicos.map(s => ({ ...s, tempId: Math.random() })));
                    }
                    
                    fetchTechnicalReports(fullOs.id);

                } else if (osToEdit && osToEdit.cliente_id) {
                    try {
                        const clientData = await apiFetch(`/general-clients/${osToEdit.cliente_id}`);
                        if (clientData && clientData.addresses && clientData.addresses.length > 0) {
                            setClientAddressesOptions(clientData.addresses);
                        }
                        setContatoNome(clientData.billing_contact_name || '');
                        setContatoEmail(clientData.billing_email || clientData.email || '');
                    } catch(e) {}
                }
            } catch (err) {
                console.error("Erro ao inicializar modal:", err);
                setError("Erro ao carregar dados.");
            } finally {
                setInitialLoading(false);
            }
        };
        init();
        
        return () => {
            if (logoUrl) URL.revokeObjectURL(logoUrl);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (clientId) {
            apiFetch(`/general-clients/${clientId}/prices`)
                .then(data => {
                    const map = {};
                    if (data && Array.isArray(data)) data.forEach(p => { map[String(p.service_id)] = p.price; });
                    setClientPrices(map);
                })
                .catch(err => {});
        } else { setClientPrices({}); }
    }, [clientId]); 

    useEffect(() => {
        if (debouncedClientSearch && !clientId) {
            const fetchClients = async () => {
                try {
                    const data = await apiFetch(`/general-clients?search=${debouncedClientSearch}`);
                    setClientSuggestions(data || []); setShowClientSuggestions(true);
                } catch (error) {}
            };
            fetchClients();
        } else { setClientSuggestions([]); }
    }, [debouncedClientSearch, clientId]);

    const fetchTechnicalReports = async (osId) => {
        try {
            const data = await apiFetch(`/os-nsa/${osId}/technical-reports`);
            setTechnicalReports(data || []);
        } catch (error) { console.error(error); }
    };

    const getAvailableTrucks = () => {
        const truckItemsInOS = servicos.map(s => {
            const refService = allServices.find(as => as.description === s.descricao);
            return {
                ...s,
                type: refService ? refService.type : s.type,
                applied_standards: refService ? refService.applied_standards : '',
                ansi_standards: refService ? refService.ansi_standards : '', 
                service_table: refService ? refService : null, 
                test_performed: refService ? refService.test_performed : ''
            };
        }).filter(s => s.type === 'CAMINHÃO');

        const usageCounts = {};
        technicalReports.forEach(rep => {
            if ((rep.report_type === 'TENSAO_APLICADA' || rep.report_type === 'TENSAO_APLICADA_CAT_B') && rep.data?.item_servico_selecionado) {
                if (currentReport && currentReport.id === rep.id) return;
                const itemName = rep.data.item_servico_selecionado;
                usageCounts[itemName] = (usageCounts[itemName] || 0) + 1;
            }
        });

        const available = [];
        truckItemsInOS.forEach(item => {
            const used = usageCounts[item.descricao] || 0;
            const totalQty = parseFloat(item.quantidade) || 0;
            if (used < totalQty) available.push(item);
        });

        if (currentReport?.data?.item_servico_selecionado) {
            const currentItemName = currentReport.data.item_servico_selecionado;
            if (!available.some(i => i.descricao === currentItemName)) {
                const originalItem = truckItemsInOS.find(t => t.descricao === currentItemName);
                if (originalItem) available.push(originalItem);
            }
        }
        return available;
    };

    const getAvailableEpis = () => {
        const epiItemsInOS = servicos.map(s => {
            const refService = allServices.find(as => as.description === s.descricao);
            return {
                ...s,
                type: refService ? refService.type : s.type,
                applied_standards: refService ? refService.applied_standards : '',
                test_performed: refService ? refService.test_performed : '' 
            };
        }).filter(s => s.type === 'EPI' || s.type === 'EPC');

        const usageCounts = {};
        technicalReports.forEach(rep => {
            if (rep.report_type === 'EPIS' && rep.data?.item_servico_selecionado) {
                if (currentReport && currentReport.id === rep.id) return;
                const itemName = rep.data.item_servico_selecionado;
                usageCounts[itemName] = (usageCounts[itemName] || 0) + 1;
            }
        });

        const available = [];
        epiItemsInOS.forEach(item => {
            const used = usageCounts[item.descricao] || 0;
            const totalQty = parseFloat(item.quantidade) || 0;
            if (used < totalQty) available.push(item);
        });

        if (currentReport?.data?.item_servico_selecionado) {
            const currentItemName = currentReport.data.item_servico_selecionado;
            if (!available.some(i => i.descricao === currentItemName)) {
                const originalItem = epiItemsInOS.find(t => t.descricao === currentItemName);
                if (originalItem) available.push(originalItem);
            }
        }
        return available;
    };

    const handleTruckSelect = (itemName) => {};
    const handleEpiSelect = (itemName) => {};

    // --- MANIPULAÇÃO DE LAUDOS ---
    const handleOpenAddLaudoModal = () => {
        setLaudoStep('type'); 
        setShowReportTypeSelection(true);
    };

    const handleSelectTensaoAplicadaType = () => {
        setLaudoStep('category'); 
    };

    const handleSelectCategory = (categoryType) => {
        setTempSelectedType(categoryType);
        setLaudoStep('language');
    };

    const handleConfirmLaudoType = async (selectedType, language = 'pt') => {
        setLoading(true);
        let cnpjCliente = '';
        let nextRef = osToEdit?.os_number || ''; 

        if (clientId) {
            try {
                const clientData = await apiFetch(`/general-clients/${clientId}`);
                cnpjCliente = clientData.document_id || ''; 

                if (selectedType === 'TENSAO_APLICADA' || selectedType === 'TENSAO_APLICADA_CAT_B') {
                    const refData = await apiFetch(`/os-nsa/next-report-reference/${clientId}`);
                    if (refData && refData.nextReference) {
                        nextRef = refData.nextReference;
                    }
                }
            } catch (error) {
                console.error("Erro ao buscar dados para novo laudo:", error);
            }
        }

        const initialData = {
            language: language, 
            referencia: nextRef, 
            data_emissao_laudo: formatDateToBR(getTodayLocalISO()),
            cliente_nome: clientSearch,
            cliente_cnpj: cnpjCliente,
            cnpj_automatico: cnpjCliente,
            cliente_gestor: contatoNome,
            cliente_telefone: '',
            area_atendida: clientSearch,
            pedido: osToEdit?.os_number || '', 
            data_ensaio: formatDateToBR(getTodayLocalISO()),
            local: clientSearch,
            temperatura: '',
            umidade: '',
            norma_aplicavel: 'ASTM F 711',
            item_servico_selecionado: '',
            texto_norma_cesta: 'ABNT NBR 16.092',
            texto_norma_liner: 'ABNT NBR 16.092',
            titulo_ensaio_nome: 'ENSAIO EM CESTA AÉREA ISOLADA',
            titulo_ensaio_norma: 'ABNT NBR 16.092',
            photos: Array(8).fill('')
        };

        setCurrentReport({ data: initialData });
        setReportType(selectedType);
        setShowReportTypeSelection(false);
        setShowReportEditor(true);
        setLoading(false);
    };

    const handleEditTechnicalReport = (report) => {
        if (!report.data.photos) report.data.photos = Array(8).fill('');
        setCurrentReport(report);
        setReportType(report.report_type);
        setShowReportEditor(true);
    };

    const handleDeleteTechnicalReport = async (reportId) => {
        if (!window.confirm("Tem certeza que deseja excluir este laudo técnico?")) return;
        try {
            await apiFetch(`/os-nsa/${osToEdit.id}/technical-reports/${reportId}`, { method: 'DELETE' });
            fetchTechnicalReports(osToEdit.id);
        } catch (error) { alert("Erro ao excluir laudo."); }
    };

    const handleSaveTechnicalReport = async () => {
        if (!currentReport || !currentReport.data) return;
        if ((reportType === 'TENSAO_APLICADA' || reportType === 'TENSAO_APLICADA_CAT_B' || reportType === 'EPIS') && !currentReport.data.item_servico_selecionado) {
            alert("ERRO: Por favor, selecione um Item da OS antes de salvar o laudo.");
            return; 
        }
        try {
            setLoading(true);
            const payload = {
                report_id: currentReport.id,
                report_type: reportType,
                data: currentReport.data
            };
            await apiFetch(`/os-nsa/${osToEdit.id}/technical-reports`, { method: 'POST', body: payload });
            setShowReportEditor(false);
            fetchTechnicalReports(osToEdit.id);
        } catch (error) { alert("Erro ao salvar laudo técnico: " + error.message); } finally { setLoading(false); }
    };

    const getReportsAvailableForPhotos = () => {
        return technicalReports.filter(report => {
            if (report.report_type !== 'TENSAO_APLICADA' && report.report_type !== 'TENSAO_APLICADA_CAT_B') return false;
            const hasPhotos = report.data.photos && report.data.photos.some(p => p && p.trim() !== '');
            return !hasPhotos;
        });
    };

    const handleSelectReportForPhotos = () => {
        if (!selectedReportIdForPhotos) return;
        const report = technicalReports.find(r => r.id === parseInt(selectedReportIdForPhotos));
        if (report) {
            if (!report.data.photos) report.data.photos = Array(8).fill('');
            setEditingPhotosForReport(report);
        }
    };

    const handlePhotoEditChange = (index, value) => {
        if (!editingPhotosForReport) return;
        const newPhotos = [...editingPhotosForReport.data.photos];
        newPhotos[index] = value;
        setEditingPhotosForReport({
            ...editingPhotosForReport,
            data: { ...editingPhotosForReport.data, photos: newPhotos }
        });
    };

    const handleSavePhotosToReport = async () => {
        if (!editingPhotosForReport) return;
        try {
            setLoading(true);
            const payload = {
                report_id: editingPhotosForReport.id,
                report_type: editingPhotosForReport.report_type,
                data: editingPhotosForReport.data
            };
            await apiFetch(`/os-nsa/${osToEdit.id}/technical-reports`, { method: 'POST', body: payload });
            
            fetchTechnicalReports(osToEdit.id);
            setEditingPhotosForReport(null);
            setSelectedReportIdForPhotos('');
            alert("Fotos salvas com sucesso!");
        } catch (error) {
            alert("Erro ao salvar fotos: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectClient = (client) => {
        setClientId(client.id); 
        setClientSearch(client.name);
        
        if (client.addresses && client.addresses.length > 0) {
            setClientAddressesOptions(client.addresses);
            setClienteEndereco(formatAddressString(client.addresses[0]));
        } else {
            setClientAddressesOptions([]);
            setClienteEndereco(`${client.address_street || ''}, ${client.address_number || ''} - ${client.address_city || ''}`);
        }

        setContatoNome(client.billing_contact_name || ''); 
        setContatoEmail(client.billing_email || client.email || ''); 
        setShowClientSuggestions(false);
    };

    const handleClientSearchChange = (e) => { 
        setClientSearch(e.target.value); 
        setClientId(null); 
        setClientPrices({}); 
        setClientAddressesOptions([]);
    };

    const handleServiceChange = (index, field, value) => {
        const newServicos = [...servicos]; newServicos[index][field] = value;
        if (field === 'quantidade' || field === 'valor_unitario') {
            const qtd = parseFloat(field === 'quantidade' ? value : newServicos[index].quantidade) || 0;
            const unit = parseFloat(field === 'valor_unitario' ? value : newServicos[index].valor_unitario) || 0;
            newServicos[index].valor_total = qtd * unit;
        }
        setServicos(newServicos);
    };

    const handleServiceSelect = (index, service, price) => {
        const newServicos = [...servicos]; const item = newServicos[index];
        item.descricao = service.description; item.valor_unitario = price; item.type = service.type; 
        item.valor_total = (parseFloat(item.quantidade) || 0) * price;
        setServicos(newServicos);
    };

    const handleAddService = () => setServicos([...servicos, { tempId: Math.random(), descricao: '', quantidade: 1, valor_unitario: 0, valor_total: 0 }]);
    const handleRemoveService = (index) => { if (servicos.length > 1) setServicos(servicos.filter((_, i) => i !== index)); };
    
    const toggleFaturamentoLock = () => {
        const total = parseInt(totalLaudos) || 0; const totalCriados = technicalReports.length;
        if (totalCriados < total) { alert(`Ainda faltam laudos. Total esperado: ${total}, Criados: ${totalCriados}`); return; }
        setFaturamentoUnlocked(!faturamentoUnlocked);
    };

    const handleFaturar = () => {
        if (isFaturada || !isEditing || !faturamentoUnlocked) { alert("Condições de faturamento não atendidas."); return; }
        if (onFaturar) onFaturar({ os_number: osToEdit.os_number, valor_total: totalOS, cliente_id: clientId, cliente_nome: clientSearch });
    };

    const totalOS = servicos.reduce((acc, curr) => acc + (curr.valor_total || 0), 0);

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();
        
        if (isClient) return; 
        
        if (isFaturada) {
            alert("Atenção: Esta OS já está faturada e não pode ser editada.");
            return;
        }
        
        if (!clientId) {
            alert("Atenção: Por favor, selecione um Cliente antes de salvar a Ordem de Serviço.");
            return;
        }
        
        const validServices = servicos.filter(s => s.descricao && s.descricao.trim() !== '');
        
        if (validServices.length === 0) {
            alert("Atenção: Por favor, adicione e preencha a descrição de pelo menos um Item/Serviço antes de salvar a Ordem de Serviço.");
            return;
        }

        setLoading(true); 
        setError('');
        
        const payload = {
            cliente_id: clientId, contato_nome: contatoNome, contato_email: contatoEmail, cliente_endereco: clienteEndereco,
            data_emissao: formatDateForSubmission(dataEmissao), data_prevista: formatDateForSubmission(dataPrevista) || 'À COMBINAR',
            dias_conclusao: diasConclusao || null, deslocamento_km: parseFloat(deslocamentoKm) || 0, valor_total: totalOS,
            servicos: validServices.map(({ tempId, ...rest }) => rest), 
            tracking: [], laudos_unlocked: 0, 
            total_laudos: parseInt(totalLaudos) || 0, faturamento_unlocked: faturamentoUnlocked, laudos: [],
            photos: JSON.stringify([]),
            os_status: osStatus
        };

        try {
            let osId;
            if (isEditing) { 
                await apiFetch(`/os-nsa/${osToEdit.id}`, { method: 'PUT', body: payload }); 
                osId = osToEdit.id; 
            } else { 
                const response = await apiFetch('/os-nsa', { method: 'POST', body: payload }); 
                osId = response.id; 
            }
            if (onSave) await onSave(osId); else onClose();
        } catch (err) { 
            alert("Erro ao salvar: " + (err.message || "Verifique os dados e tente novamente."));
            setError(err.message || "Erro ao salvar."); 
        } finally { 
            setLoading(false); 
        }
    };

    const evaluateTensaoAplicadaStatus = (data, reportType) => {
        if (!data) return null;

        let mainFields = [];
        let linerFields = [];

        if (reportType === 'TENSAO_APLICADA_CAT_B') {
            mainFields = [
                data.resultado_continuidade,
                data.lanca_q_res_1,
                data.lanca_q_res_2,
                data.chassi_q_res_1,
                data.chassi_q_res_2,
                data.chassi_q_res_3,
                data.chassi_q_res_4,
                data.lanca_p_res_1,
                data.chassi_p_res_1
            ];
            linerFields = [
                data.liner_qualif_resultado,
                data.liner_period_res
            ];
        } else {
            mainFields = [
                data.lanca_qualif_resultado,
                data.chassi_qualif_resultado,
                data.lanca_period_resultado,
                data.chassi_period_resultado
            ];
            linerFields = [
                data.liner_qualif_resultado,
                data.liner_period_res
            ];
        }

        if (mainFields.includes('REPROVADO')) {
            return 'REPROVADO';
        }
        if (mainFields.includes('APROVADO')) {
            return 'APROVADO';
        }
        if (linerFields.includes('REPROVADO')) {
            return 'REPROVADO';
        }
        if (linerFields.includes('APROVADO')) {
            return 'APROVADO';
        }

        return null;
    };

    // --- NOVA LÓGICA DE AGRUPAMENTO DE LAUDOS ---
    const getGroupedReports = () => {
        const groups = {};

        technicalReports.forEach(report => {
            let typeName = '';
            let status = '';

            if (report.report_type === 'EPIS') {
                typeName = "Laudo de EPI's";
                status = report.data?.status_equipamento || 'APROVADO';
            } else if (report.report_type === 'TENSAO_APLICADA') {
                typeName = "Ensaio Elétrico de Tensão Aplicada (Cat C)";
                status = evaluateTensaoAplicadaStatus(report.data, report.report_type) || 'APROVADO';
            } else if (report.report_type === 'TENSAO_APLICADA_CAT_B') {
                typeName = "Ensaio Elétrico de Tensão Aplicada (Cat B)";
                status = evaluateTensaoAplicadaStatus(report.data, report.report_type) || 'APROVADO';
            } else {
                typeName = "Outros Laudos";
                status = 'APROVADO';
            }

            const groupKey = `${typeName}:::${status}`;

            if (!groups[groupKey]) {
                groups[groupKey] = {
                    typeName: typeName,
                    status: status,
                    reports: []
                };
            }
            groups[groupKey].reports.push(report);
        });

        // Retorna um array ordenado: Primeiro os reprovados, depois os aprovados, organizados por nome
        return Object.entries(groups)
            .map(([key, value]) => ({ key, ...value }))
            .sort((a, b) => {
                if (a.status === 'REPROVADO' && b.status !== 'REPROVADO') return -1;
                if (a.status !== 'REPROVADO' && b.status === 'REPROVADO') return 1;
                return a.typeName.localeCompare(b.typeName);
            });
    };

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    if (initialLoading) return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"><Loader2 className="animate-spin h-8 w-8 text-blue-600" /></div>;

    const TabButton = ({ id, label, icon: Icon, disabled }) => (
        <button type="button" onClick={() => !disabled && setActiveTab(id)} disabled={disabled} className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === id ? 'border-blue-600 text-blue-600 bg-blue-50' : disabled ? 'border-transparent text-gray-300 cursor-not-allowed' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'} shrink-0`}>
            {Icon && <Icon size={16} />} {label} {disabled && <Lock size={12} className="ml-1 text-gray-300" />}
        </button>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-2 md:p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] h-[98vh] flex flex-col overflow-hidden border border-gray-100" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b bg-gray-50 shrink-0">
                    <div>
                        <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
                            {isEditing ? `Editar OS #${osToEdit.os_number}` : 'Nova Ordem de Serviço (OS/NSA)'}
                            {isFaturada && <span className="px-2 py-0.5 text-xs font-bold text-green-700 bg-green-100 rounded-full border border-green-200">FATURADA</span>}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"><X size={24} /></button>
                </div>

                <div className="flex border-b border-gray-200 bg-white px-4 md:px-6 overflow-x-auto scrollbar-hide shrink-0">
                    {canViewGeral && <TabButton id="geral" label="Geral" icon={ClipboardList} />}
                    {canViewLaudos && <TabButton id="laudos_tecnicos" label="Laudos Técnicos" icon={FileText} />}
                    {canViewFotos && <TabButton id="fotos" label="Fotos (Por Laudo)" icon={Camera} />}
                </div>

                <form className="flex-1 overflow-y-auto p-4 md:p-6 space-y-8 bg-gray-50/50">
                    {activeTab === 'geral' && canViewGeral && (
                        <div className={`space-y-8 animate-in fade-in duration-300 ${!canEditGeral ? 'opacity-80 pointer-events-none' : ''}`}>
                             <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2 space-y-4 bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200">
                                    <div className="flex items-center justify-between border-b pb-2 mb-4">
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Dados do Cliente</h3>
                                        
                                        <div className="flex items-center gap-2">
                                            <label className="text-xs font-bold text-gray-600 uppercase">Status:</label>
                                            <select 
                                                value={osStatus} 
                                                onChange={e => setOsStatus(e.target.value)}
                                                disabled={initialStatus === 'Aprovada' || !canEditGeral} 
                                                className={`text-sm font-bold border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500
                                                    ${osStatus === 'Aprovada' ? 'bg-green-50 text-green-700 border-green-300' : 
                                                      osStatus === 'Recusada' ? 'bg-red-50 text-red-700 border-red-300' : 
                                                      'bg-yellow-50 text-yellow-700 border-yellow-300'}
                                                `}
                                            >
                                                <option value="Em análise">Em análise</option>
                                                <option value="Aprovada">Aprovada</option>
                                                <option value="Recusada">Recusada</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="relative" ref={wrapperRef}>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Cliente <span className="text-red-500">*</span></label>
                                        <div className="relative">
                                            <input type="text" value={clientSearch} onChange={handleClientSearchChange} className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-100" placeholder="Pesquise por nome ou documento..." disabled={loading || !canEditGeral} />
                                            <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                                        </div>
                                        {showClientSuggestions && clientSuggestions.length > 0 && canEditGeral && (
                                            <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100">
                                                {clientSuggestions.map(client => (
                                                    <li key={client.id} onClick={() => handleSelectClient(client)} className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 transition-colors"><div className="font-medium text-gray-800">{client.name}</div></li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Contato</label><div className="relative"><input type="text" value={isRestricted ? '******' : contatoNome} onChange={e => setContatoNome(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md" disabled={isRestricted || !canEditGeral} /><User className="absolute left-3 top-2.5 text-gray-400" size={16} /></div></div>
                                            <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><div className="relative"><input type="email" value={isRestricted ? '******' : contatoEmail} onChange={e => setContatoEmail(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md" disabled={isRestricted || !canEditGeral} /><Mail className="absolute left-3 top-2.5 text-gray-400" size={16} /></div></div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Endereço da Obra/Cliente</label>
                                        <div className="relative flex items-center gap-2">
                                            {clientAddressesOptions.length > 1 ? (
                                                <select 
                                                    value={clienteEndereco} 
                                                    onChange={e => setClienteEndereco(e.target.value)} 
                                                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md" 
                                                    disabled={!canEditGeral}
                                                >
                                                    {clientAddressesOptions.map((addr, idx) => (
                                                        <option key={idx} value={formatAddressString(addr)}>
                                                            {formatAddressString(addr)}
                                                        </option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input 
                                                    type="text" 
                                                    value={clienteEndereco} 
                                                    onChange={e => setClienteEndereco(e.target.value)} 
                                                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md" 
                                                    disabled={!canEditGeral} 
                                                />
                                            )}
                                            <MapPin className="absolute left-3 top-2.5 text-gray-400" size={16} />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4 bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200 h-fit">
                                     <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider border-b border-gray-200 pb-2 mb-4">Detalhes da Execução</h3>
                                     <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Data de Emissão</label><div className="relative"><input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} className="w-full pl-9 pr-3 py-2 bg-white border border-gray-300 rounded-md" disabled={!canEditGeral} /><Calendar className="absolute left-3 top-2.5 text-gray-400" size={16} /></div></div>
                                     <div className="grid grid-cols-2 gap-3">
                                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Previsão</label><input type="text" value={dataPrevista} onChange={e => setDataPrevista(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" disabled={!canEditGeral} /></div>
                                        <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Prazo (Dias)</label><input type="number" value={diasConclusao} onChange={e => setDiasConclusao(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm" disabled={!canEditGeral} /></div>
                                     </div>
                                     <div><label className="block text-xs font-bold text-gray-500 uppercase mb-1">Deslocamento (Km)</label><div className="flex items-center"><input type="number" value={deslocamentoKm} onChange={e => setDeslocamentoKm(e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-l-md text-sm" disabled={!canEditGeral} /><span className="bg-gray-100 border border-l-0 border-gray-300 text-gray-600 px-3 py-2 rounded-r-md text-sm">km</span></div></div>
                                </div>
                            </div>
                            <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200">
                                 <div className="flex justify-between items-center border-b pb-4 mb-4"><h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Itens e Serviços</h3><button type="button" onClick={handleAddService} disabled={!canEditGeral} className="text-sm font-medium flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"><Plus size={16} /> Adicionar Item</button></div>
                                <div className="border rounded-lg max-h-[300px] overflow-y-auto relative scrollbar-thin">
                                    <table className="w-full text-sm text-left"><thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs sticky top-0 z-10 shadow-sm"><tr><th className="px-4 py-3 w-1/2">Descrição</th><th className="px-2 py-3 text-center w-20">Qtde</th><th className="px-2 py-3 text-right w-32">Vl. Unit.</th><th className="px-2 py-3 text-right w-32">Total</th><th className="px-2 py-3 text-center w-12"></th></tr></thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {servicos.map((item, idx) => (<tr key={item.tempId || Math.random()} className="bg-white hover:bg-gray-50 transition-colors"><td className="px-2 py-2 relative"><ServiceSearchInput value={item.descricao} onChange={(val) => handleServiceChange(idx, 'descricao', val)} onSelect={(service, price) => handleServiceSelect(idx, service, price)} services={allServices} clientPrices={clientPrices} disabled={!canEditGeral} hideSensitiveData={hideSensitiveData} /></td><td className="px-2 py-2"><input type="number" min="1" step="0.1" className="w-full p-1 text-center border-b border-transparent focus:border-blue-500 focus:outline-none" value={item.quantidade} onChange={e => handleServiceChange(idx, 'quantidade', e.target.value)} required disabled={!canEditGeral} /></td><td className="px-2 py-2"><input type="number" min="0" step="0.01" className="w-full p-1 text-right border-b border-transparent focus:border-blue-500 focus:outline-none" value={item.valor_unitario} onChange={e => handleServiceChange(idx, 'valor_unitario', e.target.value)} required disabled={!canEditGeral} /></td><td className="px-2 py-2 text-right font-medium text-gray-800">{isRestricted ? "******" : formatCurrency(item.valor_total)}</td><td className="px-2 py-2 text-center">{servicos.length > 1 && (<button type="button" onClick={() => handleRemoveService(idx)} disabled={!canEditGeral} className="p-1 rounded text-red-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={16} /></button>)}</td></tr>))}
                                    </tbody>
                                    <tfoot className="bg-gray-50 font-bold sticky bottom-0"><tr><td colSpan="3" className="px-4 py-3 text-right text-gray-600">VALOR TOTAL:</td><td className="px-4 py-3 text-right text-blue-700 text-lg">{isRestricted ? "******" : formatCurrency(totalOS, 'BRL')}</td><td></td></tr></tfoot>
                                    </table>
                                </div>
                            </div>
                            {canShowFaturar && (<div className="flex flex-col md:flex-row justify-end mt-4 border-t pt-4 items-center gap-4">{osToEdit?.data_faturamento && (<span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded border"> Faturado em: {formatDateTime(osToEdit.data_faturamento)} </span>)}<button type="button" onClick={handleFaturar} disabled={!isEditing || !faturamentoUnlocked || isFaturada} className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-md shadow-sm transition-all">{isFaturada ? <Lock size={18} /> : (faturamentoUnlocked ? <DollarSign size={18} /> : <Lock size={18} />)} {isFaturada ? "OS Faturada" : "Faturar"}</button></div>)}
                        </div>
                    )}

                    {/* ABA DE LAUDOS TÉCNICOS COM SANFONA */}
                    {activeTab === 'laudos_tecnicos' && canViewLaudos && (
                        <div className="space-y-6 animate-in fade-in duration-300">
                            {!isEditing ? (
                                <div className="flex flex-col items-center justify-center py-16 bg-white border border-dashed border-gray-300 rounded-lg">
                                    <div className="p-3 bg-blue-50 rounded-full mb-3"><Save size={32} className="text-blue-600" /></div>
                                    <h3 className="text-lg font-bold text-gray-800">Salve a OS primeiro</h3>
                                    <p className="text-gray-500 text-center max-w-md mt-1 px-4">Para vincular laudos técnicos, o sistema precisa gerar um número de identificação para esta Ordem de Serviço.</p>
                                    <button type="button" onClick={handleSubmit} className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 shadow-sm transition-colors">Salvar e Continuar</button>
                                </div>
                            ) : (
                                <>
                                    {/* SEÇÃO DE CONTROLE DE LAUDOS */}
                                    {!isClient && (
                                        <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex flex-wrap md:flex-nowrap justify-between items-center gap-4 mb-4">
                                            <div><h3 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Liberação Financeira</h3><p className="text-xs text-blue-700 mt-1">Defina a quantidade de laudos e libere o faturamento.</p></div>
                                            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full md:w-auto">
                                                 <div className="flex items-center gap-2"><label className="text-sm font-medium text-blue-800 whitespace-nowrap">Total Esperado:</label><div className="relative w-20"><input type="number" min="0" value={totalLaudos} onChange={(e) => setTotalLaudos(e.target.value)} className="w-full pl-2 pr-2 py-1.5 border border-blue-200 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm text-center bg-white" placeholder="0" disabled={!canEditLaudos || faturamentoUnlocked} /></div></div>
                                                 {osToEdit?.data_liberacao_faturamento && (<span className="text-xs text-blue-800 font-mono bg-blue-100 px-2 py-1 rounded border border-blue-200"> Liberado: {formatDateTime(osToEdit.data_liberacao_faturamento)} </span>)}
                                                 {canShowLiberarFaturamento && (<button type="button" onClick={toggleFaturamentoLock} disabled={!canEditLaudos} className="flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-semibold bg-green-600 text-white hover:bg-green-700 shadow-md">{faturamentoUnlocked ? (<><Lock size={16} /> Bloquear</>) : (<><CheckCircle size={16} /> Liberar Faturamento</>)}</button>)}
                                            </div>
                                        </div>
                                    )}

                                    <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200">
                                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-4 mb-4 gap-3">
                                                <div><h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Laudos Técnicos Digitais</h3><p className="text-xs text-gray-500 mt-1">Visualize ou imprima os laudos.</p></div>
                                                {!isClient && (
                                                    <button type="button" onClick={handleOpenAddLaudoModal} disabled={!canEditLaudos || loading} className="text-sm font-medium flex items-center justify-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm"><Plus size={16} /> Adicionar Laudo</button>
                                                )}
                                            </div>

                                            {technicalReports.length === 0 ? (
                                                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300"><FileCheck size={48} className="mx-auto mb-3 text-gray-300" /><p className="text-gray-500 font-medium">Nenhum laudo técnico disponível.</p></div>
                                            ) : (
                                                <div className="space-y-4">
                                                    {getGroupedReports().map((group) => {
                                                        const isExpanded = !!expandedGroups[group.key];
                                                        const isReprovado = group.status === 'REPROVADO';
                                                        const isEpis = group.typeName.includes('EPI');
                                                        
                                                        // Cor baseada no tipo e status
                                                        let iconBgColor = 'bg-blue-100';
                                                        let iconTextColor = 'text-blue-600';
                                                        if (isEpis) {
                                                            iconBgColor = 'bg-orange-100';
                                                            iconTextColor = 'text-orange-600';
                                                        }

                                                        return (
                                                            <div key={group.key} className={`border rounded-lg overflow-hidden transition-all duration-200 ${isExpanded ? 'shadow-md border-gray-300' : 'border-gray-200 hover:border-gray-300'}`}>
                                                                
                                                                {/* HEADER DA SANFONA */}
                                                                <button 
                                                                    type="button" 
                                                                    onClick={() => toggleGroup(group.key)}
                                                                    className={`w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left`}
                                                                >
                                                                    <div className="flex items-center gap-3">
                                                                        <div className={`p-2 rounded-lg ${iconBgColor} ${iconTextColor}`}>
                                                                            {isEpis ? <Shield size={20} /> : <Zap size={20} />}
                                                                        </div>
                                                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                                                            <span className="font-bold text-gray-800 text-sm md:text-base">
                                                                                {group.typeName}
                                                                            </span>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${isReprovado ? 'bg-red-50 text-red-700 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
                                                                                    {isReprovado ? 'REPROVADOS' : 'APROVADOS'}: {group.reports.length}
                                                                                </span>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className={`text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                                                                        <ChevronDown size={20} />
                                                                    </div>
                                                                </button>

                                                                {/* CONTEÚDO DA SANFONA (LISTA DE LAUDOS) */}
                                                                <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[5000px] opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}>
                                                                    <div className="p-4 bg-white border-t border-gray-200 grid gap-3">
                                                                        {group.reports.map(report => (
                                                                            <div key={report.id} className="bg-white border border-gray-100 rounded-lg p-4 flex flex-col md:flex-row items-start md:items-center justify-between hover:shadow-sm transition-shadow gap-3 shadow-[0_2px_4px_rgba(0,0,0,0.02)]">
                                                                                <div className="flex items-start gap-3 w-full">
                                                                                    <div className="text-xs text-gray-600 mt-1 w-full">
                                                                                        {report.report_type === 'TENSAO_APLICADA' || report.report_type === 'TENSAO_APLICADA_CAT_B' ? (
                                                                                            <div className="flex flex-col gap-1 w-full">
                                                                                                <span className="font-bold text-gray-800 text-sm">
                                                                                                    {report.data?.equipamento_placa ? `Placa: ${report.data.equipamento_placa}` : ''}
                                                                                                    {report.data?.equipamento_placa && report.data?.equipamento_serie ? ' | ' : ''}
                                                                                                    {report.data?.equipamento_serie ? `Chassi: ${report.data.equipamento_serie}` : ''}
                                                                                                    {(report.data?.equipamento_placa || report.data?.equipamento_serie) && report.data?.lanca_serie ? ' | ' : ''}
                                                                                                    {report.data?.lanca_serie ? `Série: ${report.data.lanca_serie}` : ''}
                                                                                                </span>
                                                                                                <div className="flex justify-between items-center text-gray-500">
                                                                                                    <span>Ref: {report.data?.referencia || 'N/A'}</span>
                                                                                                    <span>Criado em: {formatDateTime(report.created_at)}</span>
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="flex flex-col gap-1 w-full">
                                                                                                {report.data?.item_servico_selecionado && (
                                                                                                    <span className="font-bold text-gray-800 text-sm uppercase">
                                                                                                        {report.data.item_servico_selecionado}
                                                                                                    </span>
                                                                                                )}
                                                                                                <span className="font-medium text-gray-700">
                                                                                                    {(() => {
                                                                                                        const data = report.data || {};
                                                                                                        const lotes = [];
                                                                                                        for (let i = 0; i < 100; i++) { if (data[`item_${i}_lote`]) lotes.push(data[`item_${i}_lote`]); }
                                                                                                        if (lotes.length === 0) return 'Lote: -';
                                                                                                        const first = lotes[0]; const last = lotes[lotes.length - 1];
                                                                                                        const allSame = lotes.every(l => l === first);
                                                                                                        return allSame ? `Lote: ${first}` : `Lote: ${first} - ${last}`;
                                                                                                    })()}
                                                                                                </span>
                                                                                                <div className="flex justify-between items-center text-gray-500">
                                                                                                    <span>Ref: {report.data?.referencia || 'N/A'}</span>
                                                                                                    <span>Criado em: {formatDateTime(report.created_at)}</span>
                                                                                                </div>
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="flex items-center gap-2 w-full md:w-auto justify-end border-t md:border-t-0 pt-3 md:pt-0 border-gray-100">
                                                                                    {!isClient && (
                                                                                        <>
                                                                                            <button type="button" onClick={() => handleEditTechnicalReport(report)} disabled={!canEditLaudos} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Editar"><Edit size={18} /></button>
                                                                                            <button type="button" onClick={() => handleDeleteTechnicalReport(report.id)} disabled={!canEditLaudos} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md" title="Excluir"><Trash2 size={18} /></button>
                                                                                        </>
                                                                                    )}
                                                                                    
                                                                                    {/* --- NOVA TRAVA: Botão Imprimir Laudo --- */}
                                                                                    {canPrintLaudo && (
                                                                                        <button type="button" onClick={() => setReportToPrint(report)} className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-md" title="Imprimir">
                                                                                            <Printer size={18} />
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}

                    {/* ABA DE FOTOS (NOVA LÓGICA POR LAUDO) */}
                    {activeTab === 'fotos' && canViewFotos && (
                        <div className="space-y-6 animate-in fade-in duration-300">
                            {/* VISÃO GERAL: LISTA DE LAUDOS COM FOTOS E OPÇÃO DE ADICIONAR */}
                            {!editingPhotosForReport ? (
                                <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200">
                                    <div className="mb-6 border-b pb-4">
                                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Vincular Fotos a um Laudo</h3>
                                        <p className="text-xs text-gray-500 mb-4">Selecione um laudo de Tensão Aplicada para adicionar ou editar o conjunto de 8 fotos.</p>
                                        
                                        <div className="flex flex-col md:flex-row gap-4 items-end">
                                            <div className="flex-1 w-full">
                                                <label className="block text-xs font-bold text-gray-700 mb-1">Laudos Disponíveis (Sem fotos)</label>
                                                <select 
                                                    value={selectedReportIdForPhotos} 
                                                    onChange={(e) => setSelectedReportIdForPhotos(e.target.value)}
                                                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                >
                                                    <option value="">-- Selecione um laudo para preencher fotos --</option>
                                                    {getReportsAvailableForPhotos().map(rep => (
                                                        <option key={rep.id} value={rep.id}>
                                                            #{rep.id} - {rep.data?.titulo_ensaio_nome || 'Ensaio'} (Placa: {rep.data?.equipamento_placa || 'N/A'} | Chassi: {rep.data?.equipamento_serie || 'N/A'} | Série: {rep.data?.lanca_serie || 'N/A'})
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={handleSelectReportForPhotos}
                                                disabled={!selectedReportIdForPhotos}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                <Camera size={16} /> Preencher Fotos
                                            </button>
                                        </div>
                                    </div>

                                    {/* LISTA DE LAUDOS QUE JÁ POSSUEM FOTOS */}
                                    <div>
                                        <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Laudos com Fotos Vinculadas</h4>
                                        <div className="grid gap-3">
                                            {technicalReports.filter(r => (r.report_type === 'TENSAO_APLICADA' || r.report_type === 'TENSAO_APLICADA_CAT_B') && r.data.photos && r.data.photos.some(p => p && p.trim() !== '')).length === 0 && (
                                                <p className="text-sm text-gray-400 italic">Nenhum laudo possui fotos vinculadas ainda.</p>
                                            )}
                                            
                                            {technicalReports.filter(r => (r.report_type === 'TENSAO_APLICADA' || r.report_type === 'TENSAO_APLICADA_CAT_B') && r.data.photos && r.data.photos.some(p => p && p.trim() !== '')).map(report => (
                                                <div key={report.id} className="border rounded-md p-3 flex justify-between items-center bg-gray-50">
                                                    <div>
                                                        <div className="font-bold text-gray-800 text-sm">{report.data?.titulo_ensaio_nome}</div>
                                                        <div className="text-xs text-gray-500">
                                                            ID: {report.id} • 
                                                            Placa: {report.data?.equipamento_placa || 'N/A'} | 
                                                            Chassi: {report.data?.equipamento_serie || 'N/A'} | 
                                                            Série: {report.data?.lanca_serie || 'N/A'}
                                                        </div>
                                                    </div>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => setEditingPhotosForReport(report)}
                                                        className="text-blue-600 hover:text-blue-800 text-xs font-bold flex items-center gap-1 bg-white border border-blue-200 px-3 py-1.5 rounded hover:bg-blue-50"
                                                    >
                                                        <Edit size={14} /> Editar Fotos
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* MODO EDIÇÃO: 8 CAMPOS DE FOTO PARA O LAUDO SELECIONADO */
                                <div className="bg-white p-4 md:p-5 rounded-lg shadow-sm border border-gray-200 animate-in fade-in slide-in-from-right-4">
                                    <div className="flex justify-between items-center border-b pb-3 mb-4">
                                        <div>
                                            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
                                                <Camera size={18} className="text-blue-600" />
                                                Fotos do Equipamento
                                            </h3>
                                            <p className="text-xs text-gray-500">
                                                Vinculando ao laudo: <b>{editingPhotosForReport.data?.titulo_ensaio_nome}</b> (Placa: {editingPhotosForReport.data?.equipamento_placa})
                                            </p>
                                        </div>
                                        <button 
                                            type="button" 
                                            onClick={() => { setEditingPhotosForReport(null); setSelectedReportIdForPhotos(''); }}
                                            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                            {(editingPhotosForReport.data?.photos || Array(8).fill('')).map((photoUrl, index) => (
                                                <div key={index} className="flex gap-4 items-start">
                                                    <div className="flex-1">
                                                        <label className="block text-xs font-bold text-gray-500 mb-1">Foto {index + 1}</label>
                                                        <div className="flex gap-2">
                                                            <input 
                                                                type="text" 
                                                                value={photoUrl} 
                                                                onChange={(e) => handlePhotoEditChange(index, e.target.value)} 
                                                                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" 
                                                                placeholder="Cole o link da imagem aqui..." 
                                                                disabled={!canEditFotos} 
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="w-20 h-20 bg-gray-100 border rounded-md flex items-center justify-center overflow-hidden shrink-0">
                                                        {photoUrl ? ( 
                                                            <img src={getDirectImageUrl(photoUrl)} alt={`Preview ${index + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src = 'https://via.placeholder.com/80?text=Erro'; }} /> 
                                                        ) : ( 
                                                            <ImageIcon className="text-gray-300" size={24} /> 
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>

                                    <div className="flex justify-end gap-3 pt-4 border-t">
                                            <button 
                                                type="button" 
                                                onClick={() => { setEditingPhotosForReport(null); setSelectedReportIdForPhotos(''); }}
                                                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100"
                                            >
                                                Cancelar
                                            </button>
                                            <button 
                                                type="button" 
                                                onClick={handleSavePhotosToReport}
                                                disabled={loading}
                                                className="px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 shadow-md flex items-center gap-2"
                                            >
                                                {loading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Salvar Fotos
                                            </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </form>

                <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg shrink-0">
                    <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 shadow-sm transition-all">{isClient ? 'Fechar' : 'Cancelar'}</button>
                    {/* Botão Salvar desativado/escondido para Cliente */}
                    {!isClient && (
                        <button type="button" onClick={handleSubmit} disabled={loading || (!canEditGeral && !canEditLaudos && !canEditFotos) || isFaturada} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-md transition-all disabled:bg-blue-300"> {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Salvar OS/NSA </button>
                    )}
                </div>
            </div>

            {/* --- MODAL DE SELEÇÃO DE TIPO DE LAUDO E CATEGORIA --- */}
            {showReportTypeSelection && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-96 animate-in zoom-in-95 duration-200">
                        
                        {laudoStep === 'type' && (
                            <>
                                <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Selecione o Tipo de Laudo</h3>
                                <div className="space-y-3">
                                    <button onClick={handleSelectTensaoAplicadaType} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-blue-50 transition-all text-left group">
                                        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg group-hover:bg-blue-200"><Zap size={24} /></div><div><div className="font-bold text-gray-800">Ensaio Elétrico</div><div className="text-xs text-gray-500">Tensão Aplicada</div></div>
                                    </button>
                                    <button onClick={() => handleConfirmLaudoType('EPIS', 'pt')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-orange-50 transition-all text-left group">
                                        <div className="p-2 bg-orange-100 text-orange-600 rounded-lg group-hover:bg-orange-200"><Shield size={24} /></div><div><div className="font-bold text-gray-800">Laudo de EPI's</div><div className="text-xs text-gray-500">Certificado de Ensaio</div></div>
                                    </button>
                                </div>
                            </>
                        )}
                        
                        {laudoStep === 'category' && (
                            <>
                                <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Selecione a Categoria</h3>
                                <div className="space-y-3">
                                    <button onClick={() => handleSelectCategory('TENSAO_APLICADA')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-all text-left group">
                                        <div><div className="font-bold text-gray-800">Categoria C</div><div className="text-xs text-gray-500">Laudo Padrão Cesta Aérea / Caminhão</div></div>
                                    </button>
                                    <button onClick={() => handleSelectCategory('TENSAO_APLICADA_CAT_B')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-all text-left group">
                                        <div><div className="font-bold text-gray-800">Categoria B</div><div className="text-xs text-gray-500">Com Teste de Continuidade / Mom.</div></div>
                                    </button>
                                </div>
                            </>
                        )}

                        {laudoStep === 'language' && (
                            <>
                                <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">Selecione o Idioma</h3>
                                <div className="space-y-3">
                                    <button onClick={() => handleConfirmLaudoType(tempSelectedType, 'pt')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-all text-left font-medium"><Globe size={20} className="text-green-600" /> Português (Brasil)</button>
                                    <button onClick={() => handleConfirmLaudoType(tempSelectedType, 'en')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-all text-left font-medium"><Globe size={20} className="text-blue-600" /> Inglês (English)</button>
                                    <button onClick={() => handleConfirmLaudoType(tempSelectedType, 'es')} className="w-full flex items-center gap-3 p-4 border rounded-lg hover:bg-gray-50 transition-all text-left font-medium"><Globe size={20} className="text-orange-600" /> Espanhol (Español)</button>
                                </div>
                            </>
                        )}
                        
                        <button onClick={() => { 
                            if (laudoStep === 'language') setLaudoStep('category'); 
                            else if (laudoStep === 'category') setLaudoStep('type'); 
                            else setShowReportTypeSelection(false); 
                        }} className="mt-6 w-full py-2 text-sm text-gray-500 hover:text-gray-700">
                            {laudoStep === 'language' || laudoStep === 'category' ? 'Voltar' : 'Cancelar'}
                        </button>
                    </div>
                </div>
            )}

            {/* --- MODAL EDITOR DE LAUDO --- */}
            {showReportEditor && currentReport && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-70 p-2 md:p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] h-[98vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 shrink-0">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">{currentReport.id ? 'Editar Laudo Técnico' : 'Novo Laudo Técnico'}</h2>
                                <p className="text-sm text-gray-500">
                                    {reportType === 'TENSAO_APLICADA' ? 'Ensaio Elétrico de Tensão Aplicada (Cat C)' : 
                                     reportType === 'TENSAO_APLICADA_CAT_B' ? 'Ensaio Elétrico de Tensão Aplicada (Cat B)' : 
                                     'Laudo de EPI\'s'}
                                </p>
                            </div>
                            <button onClick={() => setShowReportEditor(false)} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full"><X size={24} /></button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto bg-gray-200 p-2 md:p-8 flex justify-center">
                            {reportType === 'TENSAO_APLICADA' ? (
                                <LaudoTensaoAplicada 
                                    data={currentReport.data} 
                                    language={currentReport.data?.language || 'pt'}
                                    onChange={(newData) => setCurrentReport({ ...currentReport, data: newData })}
                                    readOnly={!canEditLaudos}
                                    availableTrucks={getAvailableTrucks()}
                                    onTruckSelect={handleTruckSelect} 
                                    photos={currentReport.data?.photos || []} 
                                />
                            ) : reportType === 'TENSAO_APLICADA_CAT_B' ? (
                                <LaudoTensaoAplicadaCatB 
                                    data={currentReport.data} 
                                    language={currentReport.data?.language || 'pt'}
                                    onChange={(newData) => setCurrentReport({ ...currentReport, data: newData })}
                                    readOnly={!canEditLaudos}
                                    availableTrucks={getAvailableTrucks()}
                                    onTruckSelect={handleTruckSelect} 
                                    photos={currentReport.data?.photos || []} 
                                />
                            ) : (
                                <LaudoEpis 
                                    data={currentReport.data} 
                                    onChange={(newData) => setCurrentReport({ ...currentReport, data: newData })}
                                    readOnly={!canEditLaudos}
                                    availableEpis={getAvailableEpis()} 
                                    onEpiSelect={handleEpiSelect}
                                />
                            )}
                        </div>

                        <div className="flex justify-end gap-3 p-4 border-t bg-white shrink-0">
                            <button onClick={() => setShowReportEditor(false)} className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-100 shadow-sm transition-all">Cancelar</button>
                            {canEditLaudos && ( <button onClick={handleSaveTechnicalReport} disabled={loading} className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 shadow-md transition-all"> {loading ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />} Salvar Laudo </button> )}
                        </div>
                    </div>
                </div>
            )}

            {/* --- IMPRESSÃO --- */}
            {/* Trocamos o display: none por position absolute e opacity 0 para forçar o carregamento das fotos */}
            <div style={{ position: 'absolute', top: '-9999px', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                <div ref={reportPrintRef}>
                    {reportToPrint && reportToPrint.report_type === 'TENSAO_APLICADA' && (
                        <LaudoTensaoAplicada data={reportToPrint.data} language={reportToPrint.data?.language || 'pt'} readOnly={true} photos={reportToPrint.data?.photos || []} currentUser={activeUser} />
                    )}
                    {reportToPrint && reportToPrint.report_type === 'TENSAO_APLICADA_CAT_B' && (
                        <LaudoTensaoAplicadaCatB data={reportToPrint.data} language={reportToPrint.data?.language || 'pt'} readOnly={true} photos={reportToPrint.data?.photos || []} currentUser={activeUser} />
                    )}
                    {reportToPrint && reportToPrint.report_type === 'EPIS' && (
                        <LaudoEpis 
                            data={reportToPrint.data} 
                            readOnly={true} 
                            currentUser={activeUser} 
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default OsNsaFormModal;